<template>
  <modal ref="modal" size="sm" :title="title" modal-id="contact-message-modal">
    <template #body>
      <form :action="createPath" class="d-grid gap-3">
        <text-input :label="i18n.t('public.contact.your_name')" :value="message.senderName" autocomplete="name"
                    :required="true" input-id="contact-message-sender-name-input"
                    :validation-errors="validationErrors.senderName"
                    @changed="(value) => updateField('senderName', value)" />

        <text-input :label="i18n.t('public.contact.your_email')" :value="message.senderEmail"
                    autocomplete="email" input-type="email" :required="true"
                    input-id="contact-message-sender-email-input" :validation-errors="validationErrors.senderEmail"
                    @changed="(value) => updateField('senderEmail', value)" />

        <text-input :label="i18n.t('activerecord.attributes.member_contact_message.subject')" :value="message.subject"
                    :required="true" input-id="contact-message-subject-input"
                    :validation-errors="validationErrors.subject" @changed="(value) => updateField('subject', value)" />

        <text-input :label="i18n.t('activerecord.attributes.member_contact_message.content')" :value="message.content"
                    :multiline="true" :rows="6" :required="true" input-id="contact-message-content-input"
                    :validation-errors="validationErrors.content" @changed="(value) => updateField('content', value)" />

        <eu-data-processing-consent v-if="dataProcessingConsentEnabled"
                                    :explicit-consent-required="dataProcessingConsent.explicitConsentRequired"
                                    :label="dataProcessingConsent.label" :value="message.euDataProcessingConsent"
                                    :show-label="showDataProcessingConsentBeforeSubmit" entity-name="member_contact_message"
                                    :validation-errors="validationErrors.euDataProcessingConsent"
                                    @changed="(value) => updateField('euDataProcessingConsent', value)" />

        <div>
          <bot-protected-submit-button :label="i18n.t('buttons.send')" :submitting-label="i18n.t('buttons.sending')"
                                       captcha-action="member_contact_message_create" :submit-in-progress="submitting"
                                       button-class="btn btn-primary btn-lg w-100"
                                       :validation-errors="validationErrors" @submit="submit" />

          <div v-if="showDataProcessingConsentAfterSubmit" class="small-text mt-1" v-html="dataProcessingConsent.label" />
        </div>

        <bot-protection-disclaimer v-if="botProtectionEnabled" />
      </form>
    </template>
  </modal>
</template>

<script>
import ajax from '@/helpers/ajax'
import BotProtectedSubmitButton from '@/components/buttons/bot-protected-submit-button'
import BotProtectionDisclaimer from '@/components/inputs/bot-protection-disclaimer'
import EuDataProcessingConsent from '@/components/inputs/eu-data-processing-consent'
import Modal from './modal'
import { nextTick } from 'vue'
import { objectKeysToSnakeCase } from '@/helpers/transform-object-keys'
import TextInput from '@/components/inputs/text-input'

export default {
  components: { BotProtectionDisclaimer, BotProtectedSubmitButton, EuDataProcessingConsent, Modal, TextInput },

  props: {
    createPath: {
      type: String,
      required: true
    },
    currentUser: {
      type: Object,
      required: false
    },
    dataProcessingConsent: {
      type: Object,
      required: true
    },
    title: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      message: {
        content: '',
        euDataProcessingConsent: null,
        senderEmail: this.currentUser ? this.currentUser.email : '',
        senderName: this.currentUser ? this.currentUser.fullName : '',
        subject: ''
      },
      submitting: false,
      validationErrors: {}
    }
  },

  computed: {
    botProtectionEnabled() {
      return this.agraConfiguration.botProtection.kind !== 'none'
    },

    dataProcessingConsentEnabled() {
      return this.dataProcessingConsent.enabled
    },

    messageDataForSubmit() {
      const messageData = objectKeysToSnakeCase(this.message)
      if (this.dataProcessingConsentEnabled) {
        messageData.consent_content_version_id = this.dataProcessingConsent.consentContentVersionId
      }
      return messageData
    },

    showDataProcessingConsentAfterSubmit() {
      return this.dataProcessingConsentEnabled &&
             !this.dataProcessingConsent.explicitConsentRequired &&
             this.dataProcessingConsent.messagePosition === 'after_submit'
    },

    showDataProcessingConsentBeforeSubmit() {
      return this.dataProcessingConsent.explicitConsentRequired ||
             this.dataProcessingConsent.messagePosition === 'before_submit'
    }
  },

  methods: {
    clearForm() {
      this.message = {
        content: '',
        euDataProcessingConsent: null,
        senderEmail: this.currentUser ? this.currentUser.email : '',
        senderName: this.currentUser ? this.currentUser.fullName : '',
        subject: ''
      }
    },

    open() {
      this.$refs.modal.open()
    },

    submit({ friendlyCaptchaSolution, hcaptchaResponse, recaptchaToken, turnstileToken }) {
      this.submitting = true

      ajax.post({
        url: this.createPath,
        data: {
          member_contact_message: this.messageDataForSubmit,
          friendly_captcha_solution: friendlyCaptchaSolution,
          hcaptcha_response: hcaptchaResponse,
          recaptcha_token: recaptchaToken,
          turnstile_token: turnstileToken
        },
        successHandler: ({ requiresConfirmation }) => {
          this.validationErrors = {}
          this.$refs.modal.close()

          if (requiresConfirmation) {
            window.Agra.alertMessageSuccess(
              this.i18n.t('public.contact.confirmation_required', {email_address: this.message.senderEmail})
            )
          } else {
            window.Agra.alertMessageSuccess(this.i18n.t('public.contact.message_sent'))
          }

          nextTick(() => {
            this.clearForm()
          })
        },
        errorHandler: ({ info }) => {
          this.validationErrors = info.errors
        }
      }).then(() => {
        this.submitting = false
      })
    },

    updateField(fieldName, newValue) {
      this.message[fieldName] = newValue
    }
  }
}
</script>
